import { Badge, Tag } from "@design";
import { FileText, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { browserTimezone, ScheduleSummary } from "@/features/build/dataSources/scheduleSummary";
import type { DataSourceDraftBody, SourceTypeName } from "./useDataSourceDraft";

const str = (value: unknown) => (typeof value === "string" && value ? value : null);

/** Where a SharePoint draft points: the folder (or the chosen files), its library and site. */
function sharePointPlace(config: Record<string, unknown>): string[] {
  const items = Array.isArray(config.selected_item_names) ? (config.selected_item_names as string[]) : [];
  return [
    str(config.sharepoint_folder_path) ??
      str(config.sharepoint_folder_name) ??
      (items.length ? items.join(", ") : null),
    str(config.sharepoint_drive_name),
    str(config.sharepoint_site_url),
  ].filter((part): part is string => Boolean(part));
}

/** A data source draft in words: kind, place, pages, notes, who can use it, and the schedule. */
export function DataSourceDraftSummary({
  body,
  wantsSchedule,
  canSchedule,
}: Readonly<{ body: DataSourceDraftBody; wantsSchedule: boolean; canSchedule: boolean }>) {
  const { t } = useTranslation(["buildChat", "dataSources"]);
  const collection = body.collection ?? {};
  const type: SourceTypeName = collection.sourceType ?? "upload";
  const place = type === "sharepoint" ? sharePointPlace(collection.config ?? {}) : [];
  const schedule = body.syncSchedule;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="neutral">{t(`dataSources:types.${type}`)}</Badge>
      </div>
      {collection.description && <Text variant="body-sm">{collection.description}</Text>}
      {place.length > 0 && (
        <Text variant="body-sm" tone="secondary" className="break-words">
          {place.join(" · ")}
        </Text>
      )}
      {(body.urls?.length ?? 0) + (body.notes?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {body.urls?.map((url) => (
            <Tag key={url} icon={<Globe size={12} />}>
              {url}
            </Tag>
          ))}
          {body.notes?.map((note) => (
            <Tag key={note.title} icon={<FileText size={12} />}>
              {note.title}
            </Tag>
          ))}
        </div>
      )}
      {wantsSchedule && canSchedule && (
        <div className="space-y-1">
          {schedule && (
            <>
              <ScheduleSummary
                schedule={{
                  type: schedule.type === "weekly" ? "weekly" : "daily",
                  runAtTime: schedule.runAtTime ?? "09:00",
                  daysOfWeek: schedule.daysOfWeek ?? [],
                }}
              />
              <Text variant="caption" tone="tertiary">
                {t("buildChat:dataSource.timezone", { tz: schedule.timezone ?? browserTimezone() })}
              </Text>
            </>
          )}
          {body.enableAutomatedSync && (
            <Text variant="caption" tone="tertiary">
              {t("buildChat:dataSource.automated")}
            </Text>
          )}
        </div>
      )}
      {wantsSchedule && !canSchedule && (
        <Text variant="caption" tone="warning">
          {t("buildChat:dataSource.noScheduling")}
        </Text>
      )}
    </div>
  );
}
